import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import DateTimeDisplay from './DateTimeDisplay';
import { API_ENDPOINTS } from '../config/api';
import VerifiedBankAccountFields from './VerifiedBankAccountFields';

const NonAcademicStaffManagement = () => {
  const navigate = useNavigate();
  const [staff, setStaff] = useState([]);
  const [attendanceRecords, setAttendanceRecords] = useState([]);
  const [salaryRecords, setSalaryRecords] = useState([]);
  const [feedbackRecords, setFeedbackRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('staff');
  const [selectedStaff, setSelectedStaff] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showAttendanceModal, setShowAttendanceModal] = useState(false);
  const [showSalaryModal, setShowSalaryModal] = useState(false);
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);
  const [expandedCards, setExpandedCards] = useState(new Set());
  const [expandedStaffCards, setExpandedStaffCards] = useState(new Set());
  const [searchTerm, setSearchTerm] = useState('');

  // Form states
  const [newStaff, setNewStaff] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    address: '',
    dateOfBirth: '',
    gender: '',
    department: 'Administrative Staff',
    role: '',
    hireDate: '',
    employmentStatus: 'Full-time',
    baseSalary: '',
    allowances: '',
    deductions: '',
    bankAccount: {
      accountName: '',
      bankName: '',
      bankCode: '',
      bankId: '',
      accountNumber: '',
      verified: false,
      verifiedAt: ''
    },
    emergencyContact: {
      name: '',
      relationship: '',
      phone: ''
    }
  });

  const [editStaff, setEditStaff] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    address: '',
    dateOfBirth: '',
    gender: '',
    department: 'Administrative Staff',
    role: '',
    hireDate: '',
    employmentStatus: 'Full-time',
    baseSalary: '',
    allowances: '',
    deductions: '',
    profilePicture: '',
    bankAccount: {
      accountName: '',
      bankName: '',
      bankCode: '',
      bankId: '',
      accountNumber: '',
      verified: false,
      verifiedAt: ''
    },
    emergencyContact: {
      name: '',
      relationship: '',
      phone: ''
    }
  });

  const [profilePictureFile, setProfilePictureFile] = useState(null);

  const [attendanceForm, setAttendanceForm] = useState({
    date: new Date().toISOString().split('T')[0],
    status: 'present',
    checkInTime: '08:00',
    checkOutTime: '16:00',
    notes: ''
  });

  const [salaryForm, setSalaryForm] = useState({
    month: new Date().toISOString().slice(0, 7),
    baseSalary: '',
    allowances: {
      housing: '',
      transport: '',
      medical: ''
    },
    deductions: {
      tax: '',
      insurance: ''
    },
    paymentStatus: 'pending',
    paymentDate: '',
    paymentMethod: 'bank_transfer',
    notes: ''
  });

  const [feedbackForm, setFeedbackForm] = useState({
    type: 'performance_feedback',
    title: '',
    message: '',
    priority: 'normal',
    requestDetails: {
      startDate: '',
      endDate: '',
      reason: '',
      item: '',
      justification: ''
    }
  });

  useEffect(() => {
    // Check if admin is logged in
    const adminUser = localStorage.getItem('adminUser');
    if (!adminUser) {
      navigate('/admin');
      return;
    }

    const loadData = async () => {
      setLoading(true);
      try {
        await Promise.all([
          loadStaff(),
          loadAttendanceRecords(),
          loadSalaryRecords(),
          loadFeedbackRecords()
        ]);
      } catch (error) {
        console.error('Error loading data:', error);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [navigate]);

  const loadStaff = async () => {
    try {
      const adminUser = JSON.parse(localStorage.getItem('adminUser') || '{}');
      console.log('Loading staff - Admin user:', adminUser);

      const response = await fetch(`${API_ENDPOINTS.BASE_URL}/api/staff`, {
        headers: {
          'admin-email': adminUser.email
        }
      });

      console.log('API Response status:', response.status);
      console.log('API Response ok:', response.ok);

      if (response.ok) {
        const data = await response.json();
        console.log('Staff data received:', data);
        setStaff(data);
      } else {
        const errorText = await response.text();
        console.error('API Error response:', errorText);
        alert(`Failed to load staff: ${response.status} - ${errorText}`);
      }
    } catch (error) {
      console.error('Network error loading staff:', error);
      alert('Network error: Could not connect to server. Make sure backend is running.');
    }
  };

  const loadAttendanceRecords = async () => {
    try {
      const response = await fetch(`${API_ENDPOINTS.BASE_URL}/api/staff/attendance`);
      if (response.ok) {
        const data = await response.json();
        setAttendanceRecords(data);
      }
    } catch (error) {
      console.error('Error loading attendance records:', error);
    }
  };

  const loadSalaryRecords = async () => {
    try {
      const response = await fetch(`${API_ENDPOINTS.BASE_URL}/api/staff/salaries/all`);
      if (response.ok) {
        const data = await response.json();
        setSalaryRecords(data);
      }
    } catch (error) {
      console.error('Error loading salary records:', error);
    }
  };

  const loadFeedbackRecords = async () => {
    try {
      const response = await fetch(`${API_ENDPOINTS.BASE_URL}/api/staff/feedback/all`);
      if (response.ok) {
        const data = await response.json();
        setFeedbackRecords(data);
      }
    } catch (error) {
      console.error('Error loading feedback records:', error);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text).then(() => {
      alert('Copied to clipboard!');
    }).catch(() => {
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = text;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      alert('Copied to clipboard!');
    });
  };

  const isBankAccountReady = (bankAccount = {}) => {
    const hasAnyBankDetail = Boolean(bankAccount.accountNumber || bankAccount.bankCode || bankAccount.bankName || bankAccount.accountName);
    if (!hasAnyBankDetail) return true;
    return Boolean(
      bankAccount.verified &&
      bankAccount.bankCode &&
      bankAccount.bankName &&
      bankAccount.accountNumber &&
      bankAccount.accountName
    );
  };

  const handleEditStaff = async (e) => {
    e.preventDefault();

    try {
      if (!isBankAccountReady(editStaff.bankAccount)) {
        alert('Please select the bank and verify the account number before saving bank details.');
        return;
      }

      const staffPayload = {
        firstName: editStaff.firstName || '',
        lastName: editStaff.lastName || '',
        email: editStaff.email || '',
        phone: editStaff.phone || '',
        address: editStaff.address || '',
        dateOfBirth: editStaff.dateOfBirth || '',
        gender: editStaff.gender || '',
        department: editStaff.department || '',
        role: editStaff.role || '',
        hireDate: editStaff.hireDate || '',
        employmentStatus: editStaff.employmentStatus || '',
        experience: parseInt(editStaff.experience) || 0,
        baseSalary: parseFloat(editStaff.baseSalary) || 0,
        allowances: parseFloat(editStaff.allowances) || 0,
        deductions: parseFloat(editStaff.deductions) || 0,
        bankAccount: {
          accountName: editStaff.bankAccount?.accountName || '',
          bankName: editStaff.bankAccount?.bankName || '',
          bankCode: editStaff.bankAccount?.bankCode || '',
          bankId: editStaff.bankAccount?.bankId || '',
          accountNumber: editStaff.bankAccount?.accountNumber || '',
          verified: Boolean(editStaff.bankAccount?.verified),
          verifiedAt: editStaff.bankAccount?.verifiedAt || ''
        },
        emergencyContact: editStaff.emergencyContact || {
          name: '',
          relationship: '',
          phone: ''
        }
      };

      if (profilePictureFile) {
        const formDataUpload = new FormData();
        formDataUpload.append('profilePicture', profilePictureFile);
        const uploadResponse = await fetch(`${API_ENDPOINTS.BASE_URL}/api/staff/${editStaff.id}/profile-picture`, {
          method: 'PUT',
          body: formDataUpload
        });

        if (!uploadResponse.ok) {
          const error = await uploadResponse.json();
          alert(error.error || 'Failed to update staff profile picture');
          return;
        }
      }

      const response = await fetch(`${API_ENDPOINTS.BASE_URL}/api/staff/${editStaff.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(staffPayload)
      });

      if (response.ok) {
        alert('Staff profile updated successfully!');
        setShowEditModal(false);
        setProfilePictureFile(null);
        loadStaff();
      } else {
        const error = await response.json();
        alert(error.error || 'Failed to update staff');
      }
    } catch (error) {
      console.error('Error updating staff:', error);
      alert('Failed to update staff');
    }
  };



  const handleProfilePictureChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type
      const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
      if (!allowedTypes.includes(file.type)) {
        alert('Please select a valid image file (JPEG, PNG, GIF, or WebP)');
        return;
      }

      // Validate file size (2MB limit)
      if (file.size > 2 * 1024 * 1024) {
        alert('File size must be less than 2MB');
        return;
      }

      setProfilePictureFile(file);
    }
  };

  const handleAddStaff = async (e) => {
    e.preventDefault();

    try {
      if (!isBankAccountReady(newStaff.bankAccount)) {
        alert('Please select the bank and verify the account number before saving bank details.');
        return;
      }

      const adminUser = JSON.parse(localStorage.getItem('adminUser') || '{}');
      const staffData = {
        ...newStaff,
        experience: parseInt(newStaff.experience) || 0,
        baseSalary: parseFloat(newStaff.baseSalary) || 0,
        allowances: parseFloat(newStaff.allowances) || 0,
        deductions: parseFloat(newStaff.deductions) || 0
      };

      const response = await fetch(`${API_ENDPOINTS.STAFF}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'admin-email': adminUser.email
        },
        body: JSON.stringify(staffData)
      });

      if (response.ok) {
        const result = await response.json();

        // Show login credentials to admin
        const credentials = result.loginCredentials;
        alert(`Staff member added successfully!\n\nLogin Credentials:\nEmail: ${credentials.email}\nPassword: ${credentials.password}\n\n${credentials.note}\n\nPlease provide these credentials to the staff member.`);

        setShowAddModal(false);
        setNewStaff({
          firstName: '',
          lastName: '',
          email: '',
          phone: '',
          address: '',
          dateOfBirth: '',
          gender: '',
          department: 'Administrative Staff',
          role: '',
          hireDate: '',
          employmentStatus: 'Full-time',
          baseSalary: '',
          allowances: '',
          deductions: '',
          bankAccount: {
            accountName: '',
            bankName: '',
            bankCode: '',
            bankId: '',
            accountNumber: '',
            verified: false,
            verifiedAt: ''
          },
          emergencyContact: {
            name: '',
            relationship: '',
            phone: ''
          }
        });
        loadStaff();
      } else {
        const error = await response.json();
        alert(error.error || 'Failed to add staff');
      }
    } catch (error) {
      console.error('Error adding staff:', error);
      alert('Failed to add staff');
    }
  };

  const handleAssignDepartment = async (staffId, departmentName) => {
    try {
      const adminUser = JSON.parse(localStorage.getItem('adminUser') || '{}');
      const response = await fetch(`${API_ENDPOINTS.BASE_URL}/api/staff/${staffId}/assign-department`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'admin-email': adminUser.email
        },
        body: JSON.stringify({ departmentName })
      });

      if (response.ok) {
        alert('Staff assigned to department successfully!');
        loadStaff();
      } else {
        alert('Failed to assign staff to department');
      }
    } catch (error) {
      console.error('Error assigning staff to department:', error);
      alert('Failed to assign staff to department');
    }
  };

  const handleMarkAttendance = async (e) => {
    e.preventDefault();

    if (!selectedStaff) return;

    try {
      const adminUser = JSON.parse(localStorage.getItem('adminUser') || '{}');
      const response = await fetch(`${API_ENDPOINTS.BASE_URL}/api/staff/${selectedStaff.id}/attendance`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'admin-email': adminUser.email
        },
        body: JSON.stringify(attendanceForm)
      });

      if (response.ok) {
        alert('Attendance marked successfully!');
        setShowAttendanceModal(false);
        setAttendanceForm({
          date: new Date().toISOString().split('T')[0],
          status: 'present',
          checkInTime: '08:00',
          checkOutTime: '16:00',
          notes: ''
        });
        loadAttendanceRecords();
      } else {
        const error = await response.json();
        alert(error.error || 'Failed to mark attendance');
      }
    } catch (error) {
      console.error('Error marking attendance:', error);
      alert('Failed to mark attendance');
    }
  };

  const handleAddSalary = async (e) => {
    e.preventDefault();

    if (!selectedStaff) return;

    try {
      const adminUser = JSON.parse(localStorage.getItem('adminUser') || '{}');
      const salaryData = {
        ...salaryForm,
        baseSalary: parseFloat(salaryForm.baseSalary) || 0,
        allowances: {
          housing: parseFloat(salaryForm.allowances.housing) || 0,
          transport: parseFloat(salaryForm.allowances.transport) || 0,
          medical: parseFloat(salaryForm.allowances.medical) || 0
        },
        deductions: {
          tax: parseFloat(salaryForm.deductions.tax) || 0,
          insurance: parseFloat(salaryForm.deductions.insurance) || 0
        }
      };

      const response = await fetch(`${API_ENDPOINTS.BASE_URL}/api/staff/${selectedStaff.id}/salaries`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'admin-email': adminUser.email
        },
        body: JSON.stringify(salaryData)
      });

      if (response.ok) {
        alert('Salary record added successfully!');
        setShowSalaryModal(false);
        setSalaryForm({
          month: new Date().toISOString().slice(0, 7),
          baseSalary: '',
          allowances: { housing: '', transport: '', medical: '' },
          deductions: { tax: '', insurance: '' },
          paymentStatus: 'pending',
          paymentDate: '',
          paymentMethod: 'bank_transfer',
          notes: ''
        });
        loadSalaryRecords();
      } else {
        const error = await response.json();
        alert(error.error || 'Failed to add salary record');
      }
    } catch (error) {
      console.error('Error adding salary record:', error);
      alert('Failed to add salary record');
    }
  };

  const handleSubmitFeedback = async (e) => {
    e.preventDefault();

    if (!selectedStaff) return;

    try {
      const adminUser = JSON.parse(localStorage.getItem('adminUser') || '{}');
      const feedbackData = {
        ...feedbackForm,
        submittedBy: adminUser.id,
        submittedByName: adminUser.name,
        submittedByRole: 'admin'
      };

      const response = await fetch(`${API_ENDPOINTS.BASE_URL}/api/staff/${selectedStaff.id}/feedback`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'admin-email': adminUser.email
        },
        body: JSON.stringify(feedbackData)
      });

      if (response.ok) {
        alert('Feedback/request submitted successfully!');
        setShowFeedbackModal(false);
        setFeedbackForm({
          type: 'performance_feedback',
          title: '',
          message: '',
          priority: 'normal',
          requestDetails: {
            startDate: '',
            endDate: '',
            reason: '',
            item: '',
            justification: ''
          }
        });
        loadFeedbackRecords();
      } else {
        const error = await response.json();
        alert(error.error || 'Failed to submit feedback');
      }
    } catch (error) {
      console.error('Error submitting feedback:', error);
      alert('Failed to submit feedback');
    }
  };

  const handleUpdateFeedbackStatus = async (feedbackId, status, comments) => {
    try {
      const adminUser = JSON.parse(localStorage.getItem('adminUser') || '{}');
      const response = await fetch(`${API_ENDPOINTS.BASE_URL}/api/staff/feedback/${feedbackId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'admin-email': adminUser.email
        },
        body: JSON.stringify({ status, adminComments: comments })
      });

      if (response.ok) {
        alert('Feedback status updated successfully!');
        loadFeedbackRecords();
      } else {
        alert('Failed to update feedback status');
      }
    } catch (error) {
      console.error('Error updating feedback status:', error);
      alert('Failed to update feedback status');
    }
  };

  const handleResetPassword = async (staffMember) => {
    if (!window.confirm(`Are you sure you want to reset ${staffMember.firstName} ${staffMember.lastName}'s passwordLoading They will need to change it on next login.`)) {
      return;
    }

    try {
      const adminUser = JSON.parse(localStorage.getItem('adminUser') || '{}');
      const response = await fetch(`${API_ENDPOINTS.BASE_URL}/api/staff/change-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'admin-email': adminUser.email
        },
        body: JSON.stringify({
          email: staffMember.email,
          newPassword: 'staff123'
        })
      });

      if (response.ok) {
        alert(`Password reset successfully! ${staffMember.firstName} ${staffMember.lastName} can now login with:\nEmail: ${staffMember.email}\nPassword: staff123`);
      } else {
        const error = await response.json();
        alert(error.error || 'Failed to reset password');
      }
    } catch (error) {
      console.error('Error resetting password:', error);
      alert('Failed to reset password');
    }
  };

  const handleDeleteStaff = async (staffId) => {
    if (!window.confirm('Are you sure you want to delete this staff memberLoading This action cannot be undone.')) {
      return;
    }

    try {
      const adminUser = JSON.parse(localStorage.getItem('adminUser') || '{}');
      const response = await fetch(`${API_ENDPOINTS.BASE_URL}/api/staff/${staffId}`, {
        method: 'DELETE',
        headers: {
          'admin-email': adminUser.email
        }
      });

      if (response.ok) {
        alert('Staff member deleted successfully!');
        loadStaff();
      } else {
        const error = await response.json();
        alert(error.error || 'Failed to delete staff member');
      }
    } catch (error) {
      console.error('Error deleting staff member:', error);
      alert('Failed to delete staff member');
    }
  };

  const toggleCardExpansion = (staffId) => {
    const newExpandedCards = new Set(expandedCards);
    if (newExpandedCards.has(staffId)) {
      newExpandedCards.delete(staffId);
    } else {
      newExpandedCards.add(staffId);
    }
    setExpandedCards(newExpandedCards);
  };

  const toggleStaffCardExpansion = (staffId) => {
    const newExpandedStaffCards = new Set(expandedStaffCards);
    if (newExpandedStaffCards.has(staffId)) {
      newExpandedStaffCards.delete(staffId);
    } else {
      newExpandedStaffCards.add(staffId);
    }
    setExpandedStaffCards(newExpandedStaffCards);
  };

  const getRequestTypeLabel = (type) => {
    const labels = {
      performance_feedback: 'Performance Feedback',
      leave_request: 'Leave Request',
      equipment_request: 'Equipment Request',
      maintenance_request: 'Maintenance Request',
      other_request: 'Other Request'
    };
    return labels[type] || type;
  };

  const tabButtonClass = (tab) => `px-5 py-2.5 text-sm font-semibold rounded-lg border transition-colors whitespace-nowrap ${
    activeTab === tab
      ? 'bg-slate-900 text-white border-slate-900 shadow-sm'
      : 'bg-slate-50 text-slate-600 border-transparent hover:bg-emerald-50 hover:text-emerald-800 hover:border-emerald-100'
  }`;

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-xl text-gray-600">Loading staff management...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100 p-8">
      <div className="max-w-7xl mx-auto">
        <div className="flex justify-between items-center mb-8">
          <h1 className="text-3xl font-bold text-blue-900">Non-Academic Staff Management</h1>
          <div className="flex items-center space-x-4">
            <button
              onClick={() => navigate('/dashboard')}
              className="bg-gray-600 text-white px-4 py-2 rounded hover:bg-gray-700 transition duration-300"
            >
              Back to Dashboard
            </button>
            <button
              onClick={() => setShowAddModal(true)}
              className="bg-green-600 text-white px-6 py-2 rounded hover:bg-green-700 transition duration-300"
            >
              Add Staff Member
            </button>
            <DateTimeDisplay />
          </div>
        </div>

        {/* Tab Navigation */}
        <div className="flex flex-wrap gap-2 bg-white border border-slate-200 rounded-xl shadow-sm p-2 mb-6 overflow-x-auto">
          <button
            onClick={() => setActiveTab('staff')}
            className={tabButtonClass('staff')}
          >
            Staff ({staff.length})
          </button>
          <button
            onClick={() => setActiveTab('departments')}
            className={tabButtonClass('departments')}
          >
            Departments
          </button>
          <button
            onClick={() => setActiveTab('attendance')}
            className={tabButtonClass('attendance')}
          >
            Attendance
          </button>
          <button
            onClick={() => setActiveTab('salaries')}
            className={tabButtonClass('salaries')}
          >
            Salaries
          </button>
          <button
            onClick={() => setActiveTab('feedback')}
            className={tabButtonClass('feedback')}
          >
            Feedback & Requests ({feedbackRecords.filter(r => r.status === 'pending').length})
          </button>
        </div>

        {/* Staff Tab */}
        {activeTab === 'staff' && (
          <div>
            {/* Search Bar */}
            <div className="mb-6">
              <div className="flex items-center space-x-4">
                <div className="flex-1 relative">
                  <input
                    type="text"
                    placeholder="Search staff by name, email, phone, department, role, or employee ID..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full px-4 py-3 pl-12 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                  <svg
                    className="w-5 h-5 text-gray-400 absolute left-3 top-3.5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                </div>
                {searchTerm && (
                  <button
                    onClick={() => setSearchTerm('')}
                    className="px-4 py-3 text-gray-500 hover:text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                    title="Clear search"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                )}
              </div>
              {searchTerm && (
                <p className="text-sm text-gray-600 mt-2">
                  {(() => {
                    const filtered = staff.filter(member =>
                      (member.firstName + ' ' + member.lastName).toLowerCase().includes(searchTerm.toLowerCase()) ||
                      member.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
                      member.phone.toLowerCase().includes(searchTerm.toLowerCase()) ||
                      member.department.toLowerCase().includes(searchTerm.toLowerCase()) ||
                      (member.role || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
                      (member.employeeId || '').toLowerCase().includes(searchTerm.toLowerCase())
                    );
                    return `Found ${filtered.length} staff member${filtered.length !== 1 ? 's' : ''} matching "${searchTerm}"`;
                  })()}
                </p>
              )}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {(() => {
                const filteredStaff = staff.filter(member =>
                  (member.firstName + ' ' + member.lastName).toLowerCase().includes(searchTerm.toLowerCase()) ||
                  member.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
                  member.phone.toLowerCase().includes(searchTerm.toLowerCase()) ||
                  member.department.toLowerCase().includes(searchTerm.toLowerCase()) ||
                  (member.role || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
                  (member.employeeId || '').toLowerCase().includes(searchTerm.toLowerCase())
                );

                if (filteredStaff.length === 0 && searchTerm) {
                  return (
                    <div className="col-span-full text-center py-12">
                      <div className="text-gray-400 text-6xl mb-4">Search</div>
                      <h3 className="text-lg font-medium text-gray-600 mb-2">No staff members found</h3>
                      <p className="text-gray-500">Try adjusting your search terms or clear the search to see all staff.</p>
                      <button
                        onClick={() => setSearchTerm('')}
                        className="mt-4 bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
                      >
                        Clear Search
                      </button>
                    </div>
                  );
                }

                return filteredStaff.map((member) => {
                  const isExpanded = expandedStaffCards.has(member.id);

                  return (
                    <div key={member.id} className="bg-white rounded-lg shadow-md overflow-hidden">
                      {/* Collapsed Header - Clickable */}
                      <div
                        className="p-6 cursor-pointer hover:bg-gray-50 transition-colors duration-200"
                        onClick={() => toggleStaffCardExpansion(member.id)}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center space-x-4">
                            {/* Profile Picture */}
                            <div className="w-12 h-12 rounded-full overflow-hidden border-2 border-blue-200 shadow-sm">
                              <img
                                src={(() => {
                                  const pic = member.profilePicture;
                                  if (!pic) return '/fallback-data/default-avatar.png';
                                  // Transform localhost URLs to production URLs
                                  if (pic.startsWith('http://localhost:3005') || pic.startsWith('http://127.0.0.1:3005')) {
                                    return pic.replace(/http:\/\/(?:localhost|127\.0\.0\.1):3005/, API_ENDPOINTS.BASE_URL);
                                  }
                                  return pic;
                                })()}
                                alt={`${member.firstName} ${member.lastName}`}
                                className="w-full h-full object-cover"
                                onError={(e) => {
                                  e.target.src = '/fallback-data/default-avatar.png';
                                }}
                              />
                            </div>
                            <div>
                              <div className="flex items-center space-x-2">
                                <h3 className="text-lg font-semibold text-gray-800">
                                  {member.firstName} {member.lastName}
                                </h3>
                                {/* Feedback Notification Icon */}
                                {(() => {
                                  const memberFeedback = feedbackRecords.filter(r => r.staffId === member.id);
                                  const pendingRequests = memberFeedback.filter(r => r.status === 'pending').length;
                                  return pendingRequests > 0 ? (
                                    <div className="relative">
                                      <svg className="w-4 h-4 text-orange-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                                      </svg>
                                      <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full h-3 w-3 flex items-center justify-center">
                                        {pendingRequests}
                                      </span>
                                    </div>
                                  ) : null;
                                })()}
                              </div>
                              <p className="text-sm text-gray-600">{member.employeeId}</p>
                              <p className="text-sm text-blue-600 font-medium">{member.department}</p>
                            </div>
                          </div>
                          <div className="flex items-center space-x-3">
                            <span className={`px-2 py-1 rounded text-xs font-medium ${member.employmentStatus === 'Full-time' ? 'bg-green-100 text-green-800' : 'bg-blue-100 text-blue-800'}`}>
                              {member.employmentStatus}
                            </span>
                            {/* Expand/Collapse Icon */}
                            <svg
                              className={`w-5 h-5 text-gray-500 transform transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''}`}
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                            </svg>
                          </div>
                        </div>
                      </div>

                      {/* Expandable Content */}
                      <div className={`transition-all duration-300 ease-in-out ${isExpanded ? 'max-h-screen opacity-100' : 'max-h-0 opacity-0 overflow-hidden'}`}>
                        <div className="px-6 pb-6 border-t border-gray-100">
                          <div className="space-y-4 pt-4">
                            <div className="grid grid-cols-1 gap-3 text-sm">
                              <div className="flex justify-between items-center py-1">
                                <span className="font-medium text-gray-600">Email:</span>
                                <span className="text-gray-800">{member.email}</span>
                              </div>
                              <div className="flex justify-between items-center py-1">
                                <span className="font-medium text-gray-600">Phone:</span>
                                <span className="text-gray-800">{member.phone}</span>
                              </div>
                              {member.role && (
                                <div className="flex justify-between items-center py-1">
                                  <span className="font-medium text-gray-600">Role:</span>
                                  <span className="text-gray-800">{member.role}</span>
                                </div>
                              )}
                            </div>

                            <div className="p-3 bg-blue-50 rounded border border-blue-200">
                              <h4 className="text-sm font-medium text-blue-800 mb-2">Login Credentials</h4>
                              <div className="text-xs text-blue-700 space-y-1">
                                <p><strong>Email:</strong> {member.email}</p>
                                <p><strong>Password:</strong> staff123</p>
                                <p className="text-blue-600 italic">Default password - should be changed on first login</p>
                              </div>
                            </div>

                            <div className="flex flex-wrap gap-2">
                              <button
                            onClick={(e) => {
                              e.stopPropagation();
                                  setEditStaff({
                                    ...member,
                                    bankAccount: {
                                      accountName: member.bankAccount?.accountName || '',
                                      bankName: member.bankAccount?.bankName || '',
                                      bankCode: member.bankAccount?.bankCode || member.bankAccount?.code || '',
                                      bankId: member.bankAccount?.bankId || '',
                                      accountNumber: member.bankAccount?.accountNumber || '',
                                      verified: Boolean(member.bankAccount?.verified),
                                      verifiedAt: member.bankAccount?.verifiedAt || ''
                                    }
                                  });
                              setShowEditModal(true);
                            }}
                                className="bg-indigo-600 text-white px-3 py-1 rounded text-sm hover:bg-indigo-700"
                              >
                                Edit Profile
                              </button>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setSelectedStaff(member);
                                  setShowAttendanceModal(true);
                                }}
                                className="bg-blue-600 text-white px-3 py-1 rounded text-sm hover:bg-blue-700"
                              >
                                Mark Attendance
                              </button>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setSelectedStaff(member);
                                  setShowSalaryModal(true);
                                }}
                                className="bg-green-600 text-white px-3 py-1 rounded text-sm hover:bg-green-700"
                              >
                                Add Salary
                              </button>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setSelectedStaff(member);
                                  setShowFeedbackModal(true);
                                }}
                                className="bg-purple-600 text-white px-3 py-1 rounded text-sm hover:bg-purple-700"
                              >
                                Send Feedback
                              </button>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleResetPassword(member);
                                }}
                                className="bg-yellow-600 text-white px-3 py-1 rounded text-sm hover:bg-yellow-700"
                              >
                                Reset Password
                              </button>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleDeleteStaff(member.id);
                                }}
                                className="bg-red-600 text-white px-3 py-1 rounded text-sm hover:bg-red-700"
                              >
                                Delete Staff
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                });
              })()}
            </div>
          </div>
        )}

        {/* Departments Tab */}
        {activeTab === 'departments' && (
          <div className="space-y-6">
            {[
              'Administrative Staff',
              'Security Personnel',
              'Librarians',
              'IT Support',
              'Maintenance Staff',
              'Cafeteria Staff',
              'Transportation Staff',
              'Medical Staff',
              'Cleaning Staff',
              'Accountants',
              'Receptionists'
            ].map((department) => {
              const departmentStaff = staff.filter(s => s.department === department);
              return (
                <div key={department} className="bg-white rounded-lg shadow-md p-6">
                  <h3 className="text-lg font-semibold text-gray-800 mb-4">
                    {department} ({departmentStaff.length} members)
                  </h3>

                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {departmentStaff.map((member) => (
                      <div key={member.id} className="border rounded-lg p-4">
                        <h4 className="font-medium text-gray-800">
                          {member.firstName} {member.lastName}
                        </h4>
                        <p className="text-sm text-gray-600">{member.role}</p>
                        <p className="text-sm text-gray-600">{member.employeeId}</p>
                      </div>
                    ))}
                  </div>

                  {departmentStaff.length === 0 && (
                    <p className="text-gray-500 italic">No staff members in this department</p>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* Attendance Tab */}
        {activeTab === 'attendance' && (
          <div className="space-y-6">
            {/* Attendance Summary */}
            <div className="bg-white rounded-lg shadow-md p-6">
              <h2 className="text-xl font-semibold text-gray-800 mb-4">Attendance Summary</h2>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="text-center p-4 bg-blue-50 rounded-lg">
                  <div className="text-2xl font-bold text-blue-600">
                    {attendanceRecords.filter(r => r.date === new Date().toISOString().split('T')[0] && r.status === 'present').length}
                  </div>
                  <div className="text-sm text-gray-600">Present Today</div>
                </div>
                <div className="text-center p-4 bg-red-50 rounded-lg">
                  <div className="text-2xl font-bold text-red-600">
                    {attendanceRecords.filter(r => r.date === new Date().toISOString().split('T')[0] && r.status === 'absent').length}
                  </div>
                  <div className="text-sm text-gray-600">Absent Today</div>
                </div>
                <div className="text-center p-4 bg-green-50 rounded-lg">
                  <div className="text-2xl font-bold text-green-600">
                    {attendanceRecords.filter(r => r.location && r.location.withinRange).length}
                  </div>
                  <div className="text-sm text-gray-600">GPS Validated</div>
                </div>
                <div className="text-center p-4 bg-purple-50 rounded-lg">
                  <div className="text-2xl font-bold text-purple-600">
                    {attendanceRecords.filter(r => r.autoChecked).length}
                  </div>
                  <div className="text-sm text-gray-600">Auto Check-ins</div>
                </div>
              </div>
            </div>

            {/* Individual Staff Attendance */}
            {staff.map((member) => {
              const memberAttendance = attendanceRecords.filter(r => r.staffId === member.id);
              const todayAttendance = memberAttendance.find(r => r.date === new Date().toISOString().split('T')[0]);
              const presentCount = memberAttendance.filter(r => r.status === 'present').length;
              const totalDays = memberAttendance.length;
              const attendanceRate = totalDays > 0 ? Math.round((presentCount / totalDays) * 100) : 0;

              return (
                <div key={member.id} className="bg-white rounded-lg shadow-md p-6">
                  <div className="flex justify-between items-start mb-4">
                    <div>
                      <h3 className="text-lg font-semibold text-gray-800">
                        {member.firstName} {member.lastName}
                      </h3>
                      <p className="text-sm text-gray-600">{member.employeeId}</p>
                    </div>
                    <div className="text-right">
                      <div className="text-sm text-gray-600">Attendance Rate</div>
                      <div className={`text-lg font-bold ${attendanceRate >= 80 ? 'text-green-600' : attendanceRate >= 60 ? 'text-yellow-600' : 'text-red-600'}`}>
                        {attendanceRate}%
                      </div>
                    </div>
                  </div>

                  {/* Today's Attendance Status */}
                  <div className="mb-4">
                    <h4 className="text-sm font-medium text-gray-700 mb-2">Today's Status</h4>
                    {todayAttendance ? (
                      <div className="flex items-center space-x-4">
                        <span className={`px-3 py-1 rounded text-sm font-medium ${
                          todayAttendance.status === 'present'
                            ? 'bg-green-100 text-green-800'
                            : todayAttendance.status === 'late'
                            ? 'bg-yellow-100 text-yellow-800'
                            : 'bg-red-100 text-red-800'
                        }`}>
                          {todayAttendance.status.charAt(0).toUpperCase() + todayAttendance.status.slice(1)}
                        </span>
                        {todayAttendance.checkInTime && (
                          <span className="text-sm text-gray-600">
                            Check-in: {todayAttendance.checkInTime}
                          </span>
                        )}
                        {todayAttendance.location && (
                          <span className={`text-xs px-2 py-1 rounded ${
                            todayAttendance.location.withinRange
                              ? 'bg-green-100 text-green-700'
                              : 'bg-red-100 text-red-700'
                          }`}>
                            {todayAttendance.location.withinRange ? 'GPS ?' : 'GPS ?'}
                          </span>
                        )}
                        {todayAttendance.autoChecked && (
                          <span className="text-xs px-2 py-1 rounded bg-blue-100 text-blue-700">
                            Auto
                          </span>
                        )}
                      </div>
                    ) : (
                      <span className="text-sm text-gray-500">No attendance marked for today</span>
                    )}
                  </div>

                  {/* Recent Attendance Records */}
                  <div>
                    <h4 className="text-sm font-medium text-gray-700 mb-3">Recent Attendance ({memberAttendance.length} records)</h4>
                    {memberAttendance.length > 0 ? (
                      <div className="space-y-2 max-h-48 overflow-y-auto">
                        {memberAttendance.slice(0, 10).map((record) => (
                          <div key={record.id} className="flex items-center justify-between p-2 bg-gray-50 rounded text-sm">
                            <div className="flex items-center space-x-3">
                              <span className="text-gray-600">{record.date}</span>
                              <span className={`px-2 py-1 rounded text-xs font-medium ${
                                record.status === 'present'
                                  ? 'bg-green-100 text-green-800'
                                  : record.status === 'late'
                                  ? 'bg-yellow-100 text-yellow-800'
                                  : 'bg-red-100 text-red-800'
                              }`}>
                                {record.status}
                              </span>
                              {record.checkInTime && (
                                <span className="text-gray-600 text-xs">
                                  {record.checkInTime}
                                  {record.checkOutTime && ` - ${record.checkOutTime}`}
                                </span>
                              )}
                            </div>
                            <div className="flex items-center space-x-2">
                              {record.location && (
                                <span className={`text-xs px-2 py-1 rounded ${
                                  record.location.withinRange
                                    ? 'bg-green-100 text-green-700'
                                    : 'bg-red-100 text-red-700'
                                }`}>
                                  GPS {record.location.withinRange ? '?' : '?'}
                                </span>
                              )}
                              {record.autoChecked && (
                                <span className="text-xs px-2 py-1 rounded bg-blue-100 text-blue-700">
                                  Auto
                                </span>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-sm text-gray-500 italic">No attendance records found</p>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Salaries Tab */}
        {activeTab === 'salaries' && (
          <div className="space-y-6">
            {/* Salary Summary */}
            <div className="bg-white rounded-lg shadow-md p-6">
              <h2 className="text-xl font-semibold text-gray-800 mb-4">Salary Overview</h2>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="text-center p-4 bg-green-50 rounded-lg">
                  <div className="text-2xl font-bold text-green-600">
                    ₦{salaryRecords.reduce((sum, record) => sum + record.netSalary, 0).toLocaleString()}
                  </div>
                  <div className="text-sm text-gray-600">Total Paid This Month</div>
                </div>
                <div className="text-center p-4 bg-blue-50 rounded-lg">
                  <div className="text-2xl font-bold text-blue-600">
                    {salaryRecords.filter(r => r.paymentStatus === 'paid').length}
                  </div>
                  <div className="text-sm text-gray-600">Paid Salaries</div>
                </div>
                <div className="text-center p-4 bg-yellow-50 rounded-lg">
                  <div className="text-2xl font-bold text-yellow-600">
                    {salaryRecords.filter(r => r.paymentStatus === 'pending').length}
                  </div>
                  <div className="text-sm text-gray-600">Pending Payments</div>
                </div>
                <div className="text-center p-4 bg-red-50 rounded-lg">
                  <div className="text-2xl font-bold text-red-600">
                    {salaryRecords.filter(r => r.paymentStatus === 'overdue').length}
                  </div>
                  <div className="text-sm text-gray-600">Overdue Payments</div>
                </div>
              </div>
            </div>

            {/* Individual Staff Salaries */}
            {staff.map((member) => {
              const memberSalaries = salaryRecords.filter(r => r.staffId === member.id);
              const latestSalary = memberSalaries[0];
              const isExpanded = expandedCards.has(member.id);

              return (
                <div key={member.id} className="bg-white rounded-lg shadow-md overflow-hidden">
                  {/* Card Header */}
                  <div
                    className="p-6 cursor-pointer hover:bg-gray-50 transition-colors duration-200"
                    onClick={() => toggleCardExpansion(member.id)}
                  >
                    <div className="flex justify-between items-center">
                      <div className="flex-1">
                        <h3 className="text-lg font-semibold text-gray-800">
                          {member.firstName} {member.lastName}
                        </h3>
                        <p className="text-sm text-gray-600">{member.employeeId}</p>
                      </div>
                      <div className="flex items-center space-x-4">
                        <div className="text-center">
                          <div className="text-xs text-gray-500">Latest Salary</div>
                          <div className="font-semibold text-gray-900">
                            ₦{latestSalary?.netSalary?.toLocaleString() || 'N/A'}
                          </div>
                        </div>
                        <div className="text-center">
                          <div className="text-xs text-gray-500">Status</div>
                          <span className={`px-2 py-1 rounded text-xs font-medium ${
                            latestSalary?.paymentStatus === 'paid'
                              ? 'bg-green-100 text-green-800'
                              : latestSalary?.paymentStatus === 'pending'
                              ? 'bg-yellow-100 text-yellow-800'
                              : 'bg-red-100 text-red-800'
                          }`}>
                            {latestSalary?.paymentStatus ? latestSalary.paymentStatus.charAt(0).toUpperCase() + latestSalary.paymentStatus.slice(1) : 'No Records'}
                          </span>
                        </div>
                        <div className="text-center">
                          <div className="text-xs text-gray-500">Records</div>
                          <div className="font-semibold text-blue-600">{memberSalaries.length}</div>
                        </div>
                        <button
                          className="p-2 hover:bg-gray-100 rounded-full transition-colors duration-200"
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleCardExpansion(member.id);
                          }}
                        >
                          <svg
                            className={`w-5 h-5 text-gray-500 transform transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''}`}
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                          </svg>
                        </button>
                      </div>
                    </div>
                  </div>

                  {/* Expandable Content */}
                  <div className={`transition-all duration-300 ease-in-out ${isExpanded ? 'max-h-screen opacity-100' : 'max-h-0 opacity-0 overflow-hidden'}`}>
                    <div className="px-6 pb-6 border-t border-gray-100">
                      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                        {/* Salary Information */}
                        <div className="space-y-4">
                          <h4 className="text-md font-semibold text-gray-700">Latest Salary Information</h4>
                          {latestSalary ? (
                            <div className="space-y-3">
                              <div className="flex justify-between items-center">
                                <span className="text-sm text-gray-600">Month:</span>
                                <span className="font-medium">{latestSalary.month}</span>
                              </div>
                              <div className="grid grid-cols-2 gap-4">
                                <div className="p-3 bg-gray-50 rounded">
                                  <div className="text-xs text-gray-500">Base Salary</div>
                                  <div className="text-lg font-bold text-gray-900">
                                    ₦{latestSalary.baseSalary.toLocaleString()}
                                  </div>
                                </div>
                                <div className="p-3 bg-green-50 rounded">
                                  <div className="text-xs text-gray-500">Total Allowances</div>
                                  <div className="text-lg font-bold text-green-700">
                                    ₦{latestSalary.totalAllowances.toLocaleString()}
                                  </div>
                                </div>
                                <div className="p-3 bg-red-50 rounded">
                                  <div className="text-xs text-gray-500">Total Deductions</div>
                                  <div className="text-lg font-bold text-red-700">
                                    ₦{latestSalary.totalDeductions.toLocaleString()}
                                  </div>
                                </div>
                                <div className="p-3 bg-blue-50 rounded">
                                  <div className="text-xs text-gray-500">Net Salary</div>
                                  <div className="text-lg font-bold text-blue-700">
                                    ₦{latestSalary.netSalary.toLocaleString()}
                                  </div>
                                </div>
                              </div>
                              <div className="flex items-center space-x-4">
                                <div className={`px-3 py-1 rounded text-sm font-medium ${
                                  latestSalary.paymentStatus === 'paid'
                                    ? 'bg-green-100 text-green-800'
                                    : latestSalary.paymentStatus === 'pending'
                                    ? 'bg-yellow-100 text-yellow-800'
                                    : 'bg-red-100 text-red-800'
                                }`}>
                                  {latestSalary.paymentStatus.charAt(0).toUpperCase() + latestSalary.paymentStatus.slice(1)}
                                </div>
                                {latestSalary.paymentDate && (
                                  <span className="text-sm text-gray-600">
                                    Paid: {new Date(latestSalary.paymentDate).toLocaleDateString()}
                                  </span>
                                )}
                              </div>
                            </div>
                          ) : (
                            <div className="text-center py-8 text-gray-500">
                              <p>No salary records found</p>
                              <p className="text-sm">Add salary information using the "Add Salary" button</p>
                            </div>
                          )}
                        </div>

                        {/* Bank Account Information */}
                        <div className="space-y-4">
                          <h4 className="text-md font-semibold text-gray-700">Bank Account Details</h4>
                          {member.bankAccount && (member.bankAccount.accountName || member.bankAccount.accountNumber) ? (
                            <div className="space-y-3">
                              <div className="p-4 bg-gray-50 rounded-lg">
                                <div className="space-y-2">
                                  <div className="flex justify-between items-center">
                                    <span className="text-sm font-medium text-gray-700">Account Name:</span>
                                    <button
                                      onClick={() => copyToClipboard(member.bankAccount.accountName)}
                                      className="text-xs bg-blue-500 text-white px-2 py-1 rounded hover:bg-blue-600"
                                    >
                                      Copy
                                    </button>
                                  </div>
                                  <p className="text-gray-900 font-mono">{member.bankAccount.accountName}</p>
                                </div>
                                <div className="space-y-2 mt-3">
                                  <div className="flex justify-between items-center">
                                    <span className="text-sm font-medium text-gray-700">Bank Name:</span>
                                    <button
                                      onClick={() => copyToClipboard(member.bankAccount.bankName)}
                                      className="text-xs bg-blue-500 text-white px-2 py-1 rounded hover:bg-blue-600"
                                    >
                                      Copy
                                    </button>
                                  </div>
                                  <p className="text-gray-900">{member.bankAccount.bankName}</p>
                                </div>
                                <div className="space-y-2 mt-3">
                                  <div className="flex justify-between items-center">
                                    <span className="text-sm font-medium text-gray-700">Account Number:</span>
                                    <button
                                      onClick={() => copyToClipboard(member.bankAccount.accountNumber)}
                                      className="text-xs bg-blue-500 text-white px-2 py-1 rounded hover:bg-blue-600"
                                    >
                                      Copy
                                    </button>
                                  </div>
                                  <p className="text-gray-900 font-mono text-lg">{member.bankAccount.accountNumber}</p>
                                </div>
                              </div>
                              <div className="flex space-x-2">
                                <button
                                  onClick={() => copyToClipboard(`${member.bankAccount.accountName}\n${member.bankAccount.accountNumber}\n${member.bankAccount.bankName}`)}
                                  className="flex-1 bg-green-600 text-white px-4 py-2 rounded text-sm hover:bg-green-700"
                                >
                                  Copy All Details
                                </button>
                                <button
                                  onClick={() => copyToClipboard(`${member.firstName} ${member.lastName} - ₦${latestSalary?.netSalary?.toLocaleString() || 0}\nAccount: ${member.bankAccount.accountNumber} (${member.bankAccount.bankName})`)}
                                  className="flex-1 bg-purple-600 text-white px-4 py-2 rounded text-sm hover:bg-purple-700"
                                >
                                  Copy Payment Info
                                </button>
                              </div>
                            </div>
                          ) : (
                            <div className="text-center py-8 text-gray-500 bg-gray-50 rounded-lg">
                              <p>No bank account information</p>
                              <p className="text-sm">Update staff profile to add bank details</p>
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Action Buttons */}
                      <div className="mt-6 flex justify-end space-x-3 border-t pt-4">
                        <button
                          onClick={() => {
                            setSelectedStaff(member);
                            setShowSalaryModal(true);
                          }}
                          className="bg-green-600 text-white px-4 py-2 rounded text-sm hover:bg-green-700"
                        >
                          Add Salary Record
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Feedback & Requests Tab */}
        {activeTab === 'feedback' && (
          <div className="space-y-6">
            {/* Feedback Summary */}
            <div className="bg-white rounded-lg shadow-md p-6">
              <h2 className="text-xl font-semibold text-gray-800 mb-4">Feedback & Requests Overview</h2>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="text-center p-4 bg-yellow-50 rounded-lg">
                  <div className="text-2xl font-bold text-yellow-600">
                    {feedbackRecords.filter(r => r.status === 'pending').length}
                  </div>
                  <div className="text-sm text-gray-600">Pending Requests</div>
                </div>
                <div className="text-center p-4 bg-green-50 rounded-lg">
                  <div className="text-2xl font-bold text-green-600">
                    {feedbackRecords.filter(r => r.status === 'approved').length}
                  </div>
                  <div className="text-sm text-gray-600">Approved</div>
                </div>
                <div className="text-center p-4 bg-blue-50 rounded-lg">
                  <div className="text-2xl font-bold text-blue-600">
                    {feedbackRecords.filter(r => r.type === 'performance_feedback').length}
                  </div>
                  <div className="text-sm text-gray-600">Performance Reviews</div>
                </div>
                <div className="text-center p-4 bg-purple-50 rounded-lg">
                  <div className="text-2xl font-bold text-purple-600">
                    {feedbackRecords.filter(r => r.type !== 'performance_feedback').length}
                  </div>
                  <div className="text-sm text-gray-600">Requests</div>
                </div>
              </div>
            </div>

            {/* Feedback Records */}
            <div className="bg-white rounded-lg shadow-md p-6">
              <div className="flex justify-between items-center mb-6">
                <h3 className="text-lg font-semibold text-gray-800">All Feedback & Requests</h3>
                <button
                  onClick={() => {
                    setSelectedStaff(null);
                    setShowFeedbackModal(true);
                  }}
                  className="bg-purple-600 text-white px-4 py-2 rounded hover:bg-purple-700"
                >
                  Submit General Feedback
                </button>
              </div>

              <div className="space-y-4">
                {feedbackRecords.length === 0 ? (
                  <p className="text-gray-500 text-center py-8">No feedback or requests found</p>
                ) : (
                  feedbackRecords.map((record) => (
                    <div key={record.id} className="border rounded-lg p-4">
                      <div className="flex justify-between items-start mb-3">
                        <div className="flex-1">
                          <div className="flex items-center space-x-2 mb-2">
                            <h4 className="font-semibold text-gray-800">{record.title || getRequestTypeLabel(record.type)}</h4>
                            <span className={`px-2 py-1 rounded text-xs ${
                              record.type === 'performance_feedback' ? 'bg-blue-100 text-blue-800' :
                              record.type === 'leave_request' ? 'bg-green-100 text-green-800' :
                              record.type === 'equipment_request' ? 'bg-orange-100 text-orange-800' :
                              'bg-purple-100 text-purple-800'
                            }`}>
                              {getRequestTypeLabel(record.type)}
                            </span>
                            <span className={`px-2 py-1 rounded text-xs ${
                              record.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                              record.status === 'approved' ? 'bg-green-100 text-green-800' :
                              record.status === 'rejected' ? 'bg-red-100 text-red-800' :
                              'bg-blue-100 text-blue-800'
                            }`}>
                              {record.status.toUpperCase()}
                            </span>
                          </div>
                          <p className="text-gray-600 mb-2">{record.message}</p>
                          <div className="text-sm text-gray-500">
                            <p><strong>Staff:</strong> {record.staffName} ({record.employeeId})</p>
                            <p><strong>Submitted:</strong> {new Date(record.createdAt).toLocaleString()}</p>
                            {record.submittedByName && (
                              <p><strong>By:</strong> {record.submittedByName} ({record.submittedByRole})</p>
                            )}
                          </div>
                        </div>

                        {/* Action Buttons */}
                        {record.status === 'pending' && (
                          <div className="flex space-x-2 ml-4">
                            <button
                              onClick={() => {
                                const comments = prompt('Approval comments (optional):');
                                handleUpdateFeedbackStatus(record.id, 'approved', comments);
                              }}
                              className="bg-green-600 text-white px-3 py-1 rounded text-sm hover:bg-green-700"
                            >
                              Approve
                            </button>
                            <button
                              onClick={() => {
                                const comments = prompt('Reason for rejection:');
                                if (comments !== null) {
                                  handleUpdateFeedbackStatus(record.id, 'rejected', comments);
                                }
                              }}
                              className="bg-red-600 text-white px-3 py-1 rounded text-sm hover:bg-red-700"
                            >
                              Reject
                            </button>
                          </div>
                        )}
                      </div>

                      {/* Request Details */}
                      {record.requestDetails && (
                        <div className="mt-3 p-3 bg-gray-50 rounded">
                          <h5 className="text-sm font-medium text-gray-700 mb-2">Request Details:</h5>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm">
                            {record.requestDetails.startDate && (
                              <p><strong>Start Date:</strong> {record.requestDetails.startDate}</p>
                            )}
                            {record.requestDetails.endDate && (
                              <p><strong>End Date:</strong> {record.requestDetails.endDate}</p>
                            )}
                            {record.requestDetails.reason && (
                              <p><strong>Reason:</strong> {record.requestDetails.reason}</p>
                            )}
                            {record.requestDetails.item && (
                              <p><strong>Item:</strong> {record.requestDetails.item}</p>
                            )}
                            {record.requestDetails.justification && (
                              <p><strong>Justification:</strong> {record.requestDetails.justification}</p>
                            )}
                          </div>
                        </div>
                      )}

                      {/* Review Comments */}
                      {record.adminComments && (
                        <div className="mt-3 p-3 bg-blue-50 rounded border-l-4 border-blue-500">
                          <p className="text-sm font-medium text-blue-700">Review Comments:</p>
                          <p className="text-sm text-blue-600">{record.adminComments}</p>
                          {record.reviewedAt && (
                            <p className="text-xs text-blue-500 mt-1">
                              Reviewed: {new Date(record.reviewedAt).toLocaleString()}
                            </p>
                          )}
                        </div>
                      )}
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Add Staff Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold text-gray-900">Add New Staff Member</h2>
                <button
                  onClick={() => setShowAddModal(false)}
                  className="text-gray-400 hover:text-gray-600 text-2xl"
                >
                  Ã—
                </button>
              </div>

              <form onSubmit={handleAddStaff} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">First Name *</label>
                    <input
                      type="text"
                      value={newStaff.firstName}
                      onChange={(e) => setNewStaff({...newStaff, firstName: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Last Name *</label>
                    <input
                      type="text"
                      value={newStaff.lastName}
                      onChange={(e) => setNewStaff({...newStaff, lastName: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Email *</label>
                    <input
                      type="email"
                      value={newStaff.email}
                      onChange={(e) => setNewStaff({...newStaff, email: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Phone *</label>
                    <input
                      type="tel"
                      value={newStaff.phone}
                      onChange={(e) => setNewStaff({...newStaff, phone: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Department *</label>
                    <select
                      value={newStaff.department}
                      onChange={(e) => setNewStaff({...newStaff, department: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      required
                    >
                      <option value="Administrative Staff">Administrative Staff</option>
                      <option value="Security Personnel">Security Personnel</option>
                      <option value="Librarians">Librarians</option>
                      <option value="IT Support">IT Support</option>
                      <option value="Maintenance Staff">Maintenance Staff</option>
                      <option value="Cafeteria Staff">Cafeteria Staff</option>
                      <option value="Transportation Staff">Transportation Staff</option>
                      <option value="Medical Staff">Medical Staff</option>
                      <option value="Cleaning Staff">Cleaning Staff</option>
                      <option value="Accountants">Accountants</option>
                      <option value="Receptionists">Receptionists</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Role</label>
                    <input
                      type="text"
                      value={newStaff.role}
                      onChange={(e) => setNewStaff({...newStaff, role: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="e.g., Senior Administrator"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Hire Date</label>
                    <input
                      type="date"
                      value={newStaff.hireDate}
                      onChange={(e) => setNewStaff({...newStaff, hireDate: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Employment Status</label>
                    <select
                      value={newStaff.employmentStatus}
                      onChange={(e) => setNewStaff({...newStaff, employmentStatus: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="Full-time">Full-time</option>
                      <option value="Part-time">Part-time</option>
                      <option value="Contract">Contract</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Base Salary</label>
                    <input
                      type="number"
                      value={newStaff.baseSalary}
                      onChange={(e) => setNewStaff({...newStaff, baseSalary: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Allowances</label>
                    <input
                      type="number"
                      value={newStaff.allowances}
                      onChange={(e) => setNewStaff({...newStaff, allowances: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Deductions</label>
                    <input
                      type="number"
                      value={newStaff.deductions}
                      onChange={(e) => setNewStaff({...newStaff, deductions: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                </div>

                {/* Profile Picture Section */}
                <div className="border-t pt-4">
                  <h3 className="text-lg font-medium text-gray-900 mb-3">Bank Account Details</h3>
                  <VerifiedBankAccountFields
                    value={newStaff.bankAccount}
                    onChange={(bankAccount) => setNewStaff({...newStaff, bankAccount})}
                  />
                </div>

                <div className="border-t pt-4">
                  <h3 className="text-lg font-medium text-gray-900 mb-3">Emergency Contact</h3>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
                      <input
                        type="text"
                        value={newStaff.emergencyContact.name}
                        onChange={(e) => setNewStaff({
                          ...newStaff,
                          emergencyContact: {...newStaff.emergencyContact, name: e.target.value}
                        })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Relationship</label>
                      <input
                        type="text"
                        value={newStaff.emergencyContact.relationship}
                        onChange={(e) => setNewStaff({
                          ...newStaff,
                          emergencyContact: {...newStaff.emergencyContact, relationship: e.target.value}
                        })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
                      <input
                        type="tel"
                        value={newStaff.emergencyContact.phone}
                        onChange={(e) => setNewStaff({
                          ...newStaff,
                          emergencyContact: {...newStaff.emergencyContact, phone: e.target.value}
                        })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                  </div>
                </div>

                <div className="flex justify-end space-x-3 pt-4">
                  <button
                    type="button"
                    onClick={() => setShowAddModal(false)}
                    className="px-6 py-2 border border-gray-300 rounded-md hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                  >
                    Add Staff Member
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Edit Staff Modal */}
      {showEditModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold text-gray-900">Edit Staff Profile</h2>
                <button
                  onClick={() => setShowEditModal(false)}
                  className="text-gray-400 hover:text-gray-600 text-2xl"
                >
                  Ã—
                </button>
              </div>

              <form onSubmit={handleEditStaff} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">First Name *</label>
                    <input
                      type="text"
                      value={editStaff.firstName}
                      onChange={(e) => setEditStaff({...editStaff, firstName: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Last Name *</label>
                    <input
                      type="text"
                      value={editStaff.lastName}
                      onChange={(e) => setEditStaff({...editStaff, lastName: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Email *</label>
                    <input
                      type="email"
                      value={editStaff.email}
                      onChange={(e) => setEditStaff({...editStaff, email: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Phone *</label>
                    <input
                      type="tel"
                      value={editStaff.phone}
                      onChange={(e) => setEditStaff({...editStaff, phone: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Department *</label>
                    <select
                      value={editStaff.department}
                      onChange={(e) => setEditStaff({...editStaff, department: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      required
                    >
                      <option value="Administrative Staff">Administrative Staff</option>
                      <option value="Security Personnel">Security Personnel</option>
                      <option value="Librarians">Librarians</option>
                      <option value="IT Support">IT Support</option>
                      <option value="Maintenance Staff">Maintenance Staff</option>
                      <option value="Cafeteria Staff">Cafeteria Staff</option>
                      <option value="Transportation Staff">Transportation Staff</option>
                      <option value="Medical Staff">Medical Staff</option>
                      <option value="Cleaning Staff">Cleaning Staff</option>
                      <option value="Accountants">Accountants</option>
                      <option value="Receptionists">Receptionists</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Role</label>
                    <input
                      type="text"
                      value={editStaff.role}
                      onChange={(e) => setEditStaff({...editStaff, role: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Hire Date</label>
                    <input
                      type="date"
                      value={editStaff.hireDate}
                      onChange={(e) => setEditStaff({...editStaff, hireDate: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Employment Status</label>
                    <select
                      value={editStaff.employmentStatus}
                      onChange={(e) => setEditStaff({...editStaff, employmentStatus: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="Full-time">Full-time</option>
                      <option value="Part-time">Part-time</option>
                      <option value="Contract">Contract</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Base Salary</label>
                    <input
                      type="number"
                      value={editStaff.baseSalary}
                      onChange={(e) => setEditStaff({...editStaff, baseSalary: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Allowances</label>
                    <input
                      type="number"
                      value={editStaff.allowances}
                      onChange={(e) => setEditStaff({...editStaff, allowances: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Deductions</label>
                    <input
                      type="number"
                      value={editStaff.deductions}
                      onChange={(e) => setEditStaff({...editStaff, deductions: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                </div>

                <div className="border-t pt-4">
                  <h3 className="text-lg font-medium text-gray-900 mb-3">Bank Account Details</h3>
                  <VerifiedBankAccountFields
                    value={editStaff.bankAccount}
                    onChange={(bankAccount) => setEditStaff({...editStaff, bankAccount})}
                  />
                </div>

                {/* Profile Picture Section */}
                <div className="border-t pt-4">
                  <h3 className="text-lg font-medium text-gray-900 mb-3">Profile Picture</h3>
                  <div className="flex items-center space-x-4 mb-4">
                    <div className="w-20 h-20 rounded-full overflow-hidden border-4 border-gray-200">
                      <img
                        src={(() => {
                          const pic = editStaff.profilePicture;
                          if (!pic) return '/fallback-data/default-avatar.png';
                          // Transform localhost URLs to production URLs
                          if (pic.startsWith('http://localhost:3005') || pic.startsWith('http://127.0.0.1:3005')) {
                            return pic.replace(/http:\/\/(?:localhost|127\.0\.0\.1):3005/, API_ENDPOINTS.BASE_URL);
                          }
                          return pic;
                        })()}
                        alt={`${editStaff.firstName} ${editStaff.lastName}`}
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          e.target.src = '/fallback-data/default-avatar.png';
                        }}
                      />
                    </div>
                    <div className="flex-1">
                      <label className="block text-sm font-medium text-gray-700 mb-1">Upload New Picture</label>
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handleProfilePictureChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                      <p className="text-xs text-gray-500 mt-1">Max 2MB. Formats: JPEG, PNG, GIF, WebP</p>

                    </div>
                  </div>
                </div>

                <div className="border-t pt-4">
                  <h3 className="text-lg font-medium text-gray-900 mb-3">Emergency Contact</h3>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
                      <input
                        type="text"
                        value={editStaff.emergencyContact?.name || ''}
                        onChange={(e) => setEditStaff({
                          ...editStaff,
                          emergencyContact: {...editStaff.emergencyContact, name: e.target.value}
                        })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Relationship</label>
                      <input
                        type="text"
                        value={editStaff.emergencyContact?.relationship || ''}
                        onChange={(e) => setEditStaff({
                          ...editStaff,
                          emergencyContact: {...editStaff.emergencyContact, relationship: e.target.value}
                        })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
                      <input
                        type="tel"
                        value={editStaff.emergencyContact?.phone || ''}
                        onChange={(e) => setEditStaff({
                          ...editStaff,
                          emergencyContact: {...editStaff.emergencyContact, phone: e.target.value}
                        })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                  </div>
                </div>

                <div className="flex justify-end space-x-3 pt-4">
                  <button
                    type="button"
                    onClick={() => setShowEditModal(false)}
                    className="px-6 py-2 border border-gray-300 rounded-md hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-6 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700"
                  >
                    Update Staff
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Attendance Modal */}
      {showAttendanceModal && selectedStaff && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold text-gray-900">
                  Mark Attendance - {selectedStaff.firstName} {selectedStaff.lastName}
                </h2>
                <button
                  onClick={() => setShowAttendanceModal(false)}
                  className="text-gray-400 hover:text-gray-600 text-2xl"
                >
                  Ã—
                </button>
              </div>

              <form onSubmit={handleMarkAttendance} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Date</label>
                  <input
                    type="date"
                    value={attendanceForm.date}
                    onChange={(e) => setAttendanceForm({...attendanceForm, date: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                  <select
                    value={attendanceForm.status}
                    onChange={(e) => setAttendanceForm({...attendanceForm, status: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="present">Present</option>
                    <option value="absent">Absent</option>
                    <option value="late">Late</option>
                    <option value="half-day">Half Day</option>
                  </select>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Check In</label>
                    <input
                      type="time"
                      value={attendanceForm.checkInTime}
                      onChange={(e) => setAttendanceForm({...attendanceForm, checkInTime: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Check Out</label>
                    <input
                      type="time"
                      value={attendanceForm.checkOutTime}
                      onChange={(e) => setAttendanceForm({...attendanceForm, checkOutTime: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
                  <textarea
                    value={attendanceForm.notes}
                    onChange={(e) => setAttendanceForm({...attendanceForm, notes: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    rows="3"
                  />
                </div>
                <div className="flex justify-end space-x-3">
                  <button
                    type="button"
                    onClick={() => setShowAttendanceModal(false)}
                    className="px-6 py-2 border border-gray-300 rounded-md hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                  >
                    Mark Attendance
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Salary Modal */}
      {showSalaryModal && selectedStaff && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-lg">
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold text-gray-900">
                  Add Salary Record - {selectedStaff.firstName} {selectedStaff.lastName}
                </h2>
                <button
                  onClick={() => setShowSalaryModal(false)}
                  className="text-gray-400 hover:text-gray-600 text-2xl"
                >
                  Ã—
                </button>
              </div>

              <form onSubmit={handleAddSalary} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Month</label>
                    <input
                      type="month"
                      value={salaryForm.month}
                      onChange={(e) => setSalaryForm({...salaryForm, month: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Base Salary</label>
                    <input
                      type="number"
                      value={salaryForm.baseSalary}
                      onChange={(e) => setSalaryForm({...salaryForm, baseSalary: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      required
                    />
                  </div>
                </div>

                <div>
                  <h3 className="text-lg font-medium text-gray-900 mb-3">Allowances</h3>
                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Housing</label>
                      <input
                        type="number"
                        value={salaryForm.allowances.housing}
                        onChange={(e) => setSalaryForm({
                          ...salaryForm,
                          allowances: {...salaryForm.allowances, housing: e.target.value}
                        })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Transport</label>
                      <input
                        type="number"
                        value={salaryForm.allowances.transport}
                        onChange={(e) => setSalaryForm({
                          ...salaryForm,
                          allowances: {...salaryForm.allowances, transport: e.target.value}
                        })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Medical</label>
                      <input
                        type="number"
                        value={salaryForm.allowances.medical}
                        onChange={(e) => setSalaryForm({
                          ...salaryForm,
                          allowances: {...salaryForm.allowances, medical: e.target.value}
                        })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                  </div>
                </div>

                <div>
                  <h3 className="text-lg font-medium text-gray-900 mb-3">Deductions</h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Tax</label>
                      <input
                        type="number"
                        value={salaryForm.deductions.tax}
                        onChange={(e) => setSalaryForm({
                          ...salaryForm,
                          deductions: {...salaryForm.deductions, tax: e.target.value}
                        })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Insurance</label>
                      <input
                        type="number"
                        value={salaryForm.deductions.insurance}
                        onChange={(e) => setSalaryForm({
                          ...salaryForm,
                          deductions: {...salaryForm.deductions, insurance: e.target.value}
                        })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Payment Status</label>
                    <select
                      value={salaryForm.paymentStatus}
                      onChange={(e) => setSalaryForm({...salaryForm, paymentStatus: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="pending">Pending</option>
                      <option value="paid">Paid</option>
                      <option value="overdue">Overdue</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Payment Date</label>
                    <input
                      type="date"
                      value={salaryForm.paymentDate}
                      onChange={(e) => setSalaryForm({...salaryForm, paymentDate: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
                  <textarea
                    value={salaryForm.notes}
                    onChange={(e) => setSalaryForm({...salaryForm, notes: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    rows="3"
                  />
                </div>

                <div className="flex justify-end space-x-3">
                  <button
                    type="button"
                    onClick={() => setShowSalaryModal(false)}
                    className="px-6 py-2 border border-gray-300 rounded-md hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-6 py-2 bg-green-600 text-white rounded-md hover:bg-green-700"
                  >
                    Add Salary Record
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Feedback Modal */}
      {showFeedbackModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold text-gray-900">
                  {selectedStaff ? `Send Feedback/Request to ${selectedStaff.firstName} ${selectedStaff.lastName}` : 'Submit General Feedback'}
                </h2>
                <button
                  onClick={() => setShowFeedbackModal(false)}
                  className="text-gray-400 hover:text-gray-600 text-2xl"
                >
                  Ã—
                </button>
              </div>

              <form onSubmit={handleSubmitFeedback} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Type *</label>
                  <select
                    value={feedbackForm.type}
                    onChange={(e) => setFeedbackForm({...feedbackForm, type: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  >
                    <option value="performance_feedback">Performance Feedback</option>
                    <option value="leave_request">Leave Request</option>
                    <option value="equipment_request">Equipment Request</option>
                    <option value="maintenance_request">Maintenance Request</option>
                    <option value="other_request">Other Request</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Title *</label>
                  <input
                    type="text"
                    value={feedbackForm.title}
                    onChange={(e) => setFeedbackForm({...feedbackForm, title: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Brief title for this feedback/request"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Priority</label>
                  <select
                    value={feedbackForm.priority}
                    onChange={(e) => setFeedbackForm({...feedbackForm, priority: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="low">Low</option>
                    <option value="normal">Normal</option>
                    <option value="high">High</option>
                    <option value="urgent">Urgent</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Message *</label>
                  <textarea
                    value={feedbackForm.message}
                    onChange={(e) => setFeedbackForm({...feedbackForm, message: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    rows="4"
                    placeholder="Detailed feedback or request description"
                    required
                  />
                </div>

                {/* Conditional fields based on request type */}
                {(feedbackForm.type === 'leave_request') && (
                  <div className="border-t pt-4">
                    <h3 className="text-lg font-medium text-gray-900 mb-3">Leave Request Details</h3>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Start Date</label>
                        <input
                          type="date"
                          value={feedbackForm.requestDetails.startDate}
                          onChange={(e) => setFeedbackForm({
                            ...feedbackForm,
                            requestDetails: {...feedbackForm.requestDetails, startDate: e.target.value}
                          })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">End Date</label>
                        <input
                          type="date"
                          value={feedbackForm.requestDetails.endDate}
                          onChange={(e) => setFeedbackForm({
                            ...feedbackForm,
                            requestDetails: {...feedbackForm.requestDetails, endDate: e.target.value}
                          })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                      </div>
                    </div>
                    <div className="mt-4">
                      <label className="block text-sm font-medium text-gray-700 mb-1">Reason</label>
                      <select
                        value={feedbackForm.requestDetails.reason}
                        onChange={(e) => setFeedbackForm({
                          ...feedbackForm,
                          requestDetails: {...feedbackForm.requestDetails, reason: e.target.value}
                        })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="">Select reason</option>
                        <option value="vacation">Vacation</option>
                        <option value="sick_leave">Sick Leave</option>
                        <option value="personal">Personal</option>
                        <option value="maternity">Maternity</option>
                        <option value="bereavement">Bereavement</option>
                        <option value="other">Other</option>
                      </select>
                    </div>
                  </div>
                )}

                {(feedbackForm.type === 'equipment_request') && (
                  <div className="border-t pt-4">
                    <h3 className="text-lg font-medium text-gray-900 mb-3">Equipment Request Details</h3>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Item Requested</label>
                      <input
                        type="text"
                        value={feedbackForm.requestDetails.item}
                        onChange={(e) => setFeedbackForm({
                          ...feedbackForm,
                          requestDetails: {...feedbackForm.requestDetails, item: e.target.value}
                        })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="What equipment do you need?"
                      />
                    </div>
                    <div className="mt-4">
                      <label className="block text-sm font-medium text-gray-700 mb-1">Justification</label>
                      <textarea
                        value={feedbackForm.requestDetails.justification}
                        onChange={(e) => setFeedbackForm({
                          ...feedbackForm,
                          requestDetails: {...feedbackForm.requestDetails, justification: e.target.value}
                        })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        rows="3"
                        placeholder="Why do you need this equipment?"
                      />
                    </div>
                  </div>
                )}

                <div className="flex justify-end space-x-3 pt-4">
                  <button
                    type="button"
                    onClick={() => setShowFeedbackModal(false)}
                    className="px-6 py-2 border border-gray-300 rounded-md hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-6 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700"
                  >
                    Submit {getRequestTypeLabel(feedbackForm.type)}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default NonAcademicStaffManagement;
